/**
 * Settings → API keys (Permissions Phase 3, docs/PERMISSIONS.md §11; not designed, so built from the Settings card
 * parts with as little copy as possible). Backed by /api/dashboard/keys (worker/src/dashboard-keys.js):
 *   - create: a name, personal or one of your teams, the scopes that context may carry (labels from the Worker's
 *     SCOPES catalog), an optional expiry and allowed IPs; the token is shown once, with a copy button;
 *   - your keys with today's usage; edit (name, expiry, allowed IPs: PATCH) and revoke;
 *   - Team keys: owners and admins see, edit and revoke every key on their team;
 *   - Connected apps (OAuth, Phase 4): each app with its team and scopes; disconnect.
 */
import { useEffect, useState, type FormEvent } from "react";
import { ChevronDown, Copy, Plus } from "lucide-react";
import { Dropdown } from "../components/shared/Dropdown";
import { Card } from "./Card";
import { ListRow, RowMain, RowSub, RowTitle, RowTrail } from "../components/shared/List";
import { toastStore } from "../stores/toastStore";
import { errorText } from "../lib/apiError";

export interface KeyScope { scope: string; label: string; description: string }
export interface KeyContext {
  org: string; name: string; role: string | null; canCreate: boolean; canRevokeAll: boolean; reason: string | null;
  scopes: string[]; limits: { daily: number | null; aiDaily: number | null };
}
export interface ApiKey {
  id: string; name: string | null; prefix: string; org: { id: string; name: string | null } | null; scopes: string[]; legacy: boolean;
  createdAt: string; lastUsedAt: string | null; expiresAt: string | null; expired: boolean; ipAllow: string[];
  usageToday: { requests: number; aiCalls: number }; mine: boolean; createdBy: { name: string | null; email: string };
}
export interface ConnectedApp {
  clientId: string; name: string; org: { id: string; name: string | null } | null; scopes: string[]; connectedAt: string; lastUsedAt: string | null;
}
export interface KeysSnapshot { keys: ApiKey[]; teamKeys: ApiKey[]; contexts: KeyContext[]; scopes: KeyScope[]; apps?: ConnectedApp[] }

const fmtDay = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const n = (v: number) => v.toLocaleString("en-US");

async function call<T>(path: string, method = "GET", body?: unknown): Promise<T | null> {
  const res = await fetch(path, { method, credentials: "include", headers: body ? { "content-type": "application/json" } : undefined, body: body ? JSON.stringify(body) : undefined }).catch(() => null);
  const data = res ? await res.json().catch(() => null) : null;
  if (!res?.ok) { toastStore.getState().show({ message: errorText(data) ?? "Couldn't save that. Try again." }); return null; }
  return data as T;
}

/** "cp_live_ab12… · Ward 1 office · Public data, Team notes · Created Oct 8, 2026 · 12 requests today" */
export function keyLine(k: ApiKey, labels: Record<string, string>): string {
  return [
    `${k.prefix}…`,
    k.org ? k.org.name ?? "Team" : "Personal",
    k.scopes.map((s) => labels[s] ?? s).join(", "),
    `Created ${fmtDay(k.createdAt)}`,
    k.lastUsedAt ? `Last used ${fmtDay(k.lastUsedAt)}` : "Never used",
    k.expiresAt ? `${k.expired ? "Expired" : "Expires"} ${fmtDay(k.expiresAt)}` : null,
    k.ipAllow?.length ? `IPs ${k.ipAllow.join(", ")}` : null,
    `${n(k.usageToday.requests)} ${k.usageToday.requests === 1 ? "request" : "requests"} today`,
  ].filter(Boolean).join(" · ");
}

/** "Ward 1 office · Public data, Team notes · Connected Oct 8, 2026 · Last used Oct 9, 2026" */
export function appLine(a: ConnectedApp, labels: Record<string, string>): string {
  return [
    a.org ? a.org.name ?? "Team" : "Personal",
    a.scopes.map((s) => labels[s] ?? s).join(", "),
    `Connected ${fmtDay(a.connectedAt)}`,
    a.lastUsedAt ? `Last used ${fmtDay(a.lastUsedAt)}` : "Never used",
  ].join(" · ");
}

export function ApiKeys() {
  const [data, setData] = useState<KeysSnapshot | null>(null);
  const [creating, setCreating] = useState(false);
  const [token, setToken] = useState<{ name: string; key: string } | null>(null);
  const reload = async () => { const d = await call<KeysSnapshot>("/api/dashboard/keys"); if (d) setData(d); };
  useEffect(() => { void reload(); }, []);
  if (!data) return <Card id="api-keys" title="API keys"><ListRow as="div" className="set-row" data-busy><span aria-busy="true" /></ListRow></Card>;

  const labels = Object.fromEntries(data.scopes.map((s) => [s.scope, s.label]));
  const revoke = async (k: ApiKey) => {
    if (!(await call(`/api/dashboard/keys/${encodeURIComponent(k.id)}`, "DELETE"))) return;
    setData({ ...data, keys: data.keys.filter((x) => x.id !== k.id), teamKeys: data.teamKeys.filter((x) => x.id !== k.id) });
  };
  const disconnect = async (a: ConnectedApp) => {
    if (!(await call(`/api/dashboard/keys/apps/${encodeURIComponent(a.clientId)}`, "DELETE"))) return;
    setData({ ...data, apps: (data.apps ?? []).filter((x) => x.clientId !== a.clientId) });
  };
  const apps = data.apps ?? [];
  const copy = async () => {
    if (!token) return;
    const done = await navigator.clipboard?.writeText(token.key).then(() => true, () => false);
    toastStore.getState().show({ message: done ? "Copied" : "Couldn't copy. Select the key and copy it." });
  };
  const managesTeam = data.contexts.some((x) => x.canRevokeAll);

  return (
    <Card id="api-keys" title="API keys">
      {token && (
        <ListRow as="div" className="set-row set-token">
          <RowMain className="set-row__text">
            <RowTitle as="b">{token.name}</RowTitle>
            <code className="set-token__key">{token.key}</code>
            <RowSub wrap>Shown once.</RowSub>
          </RowMain>
          <RowTrail><button className="tset-pill" onClick={() => void copy()}><Copy size={14} />Copy</button></RowTrail>
        </ListRow>
      )}
      {creating ? (
        <CreateKey data={data} labels={labels} onCancel={() => setCreating(false)} onCreated={async (t) => { setToken(t); setCreating(false); await reload(); }} />
      ) : (
        <ListRow as="div" className="set-row">
          <RowMain className="set-row__text"><RowTitle as="b">{data.keys.length ? `${data.keys.length} ${data.keys.length === 1 ? "key" : "keys"}` : "No keys yet"}</RowTitle></RowMain>
          <RowTrail><button className="tset-pill" onClick={() => { setToken(null); setCreating(true); }} disabled={!data.contexts.some((x) => x.canCreate)}><Plus size={14} />Create key</button></RowTrail>
        </ListRow>
      )}
      {data.keys.map((k) => <KeyRow key={k.id} k={k} labels={labels} onRevoke={revoke} onSaved={reload} />)}
      {managesTeam && (
        <>
          <h3 className="set-subhead">Team keys</h3>
          {data.teamKeys.length
            ? data.teamKeys.map((k) => <KeyRow key={k.id} k={k} labels={labels} onRevoke={revoke} onSaved={reload} by />)
            : <ListRow as="div" className="set-row"><RowMain className="set-row__text"><RowSub wrap>No other keys on your team</RowSub></RowMain></ListRow>}
        </>
      )}
      {apps.length > 0 && (
        <>
          <h3 className="set-subhead">Connected apps</h3>
          {apps.map((a) => (
            <ListRow as="div" key={`${a.clientId}:${a.org?.id ?? ""}`} className="set-row">
              <RowMain className="set-row__text"><RowTitle as="b">{a.name}</RowTitle><RowSub wrap>{appLine(a, labels)}</RowSub></RowMain>
              <RowTrail><button className="tset-danger" aria-label={`Disconnect ${a.name}`} onClick={() => void disconnect(a)}>Disconnect</button></RowTrail>
            </ListRow>
          ))}
        </>
      )}
    </Card>
  );
}

function KeyRow({ k, labels, onRevoke, onSaved, by }: { k: ApiKey; labels: Record<string, string>; onRevoke: (k: ApiKey) => void; onSaved: () => Promise<void>; by?: boolean }) {
  const [editing, setEditing] = useState(false);
  if (editing) return <EditKey k={k} onCancel={() => setEditing(false)} onSaved={async () => { setEditing(false); await onSaved(); }} />;
  return (
    <ListRow as="div" className="set-row">
      <RowMain className="set-row__text">
        <RowTitle as="b">{k.name || "API key"}{by && <span className="set-key__by"> · {k.createdBy.name || k.createdBy.email}</span>}</RowTitle>
        <RowSub wrap>{keyLine(k, labels)}</RowSub>
      </RowMain>
      <RowTrail className="set-key__actions">
        <button className="tset-pill" aria-label={`Edit ${k.name || "API key"}`} onClick={() => setEditing(true)}>Edit</button>
        <button className="tset-danger" aria-label={`Revoke ${k.name || "API key"}`} onClick={() => onRevoke(k)}>Revoke</button>
      </RowTrail>
    </ListRow>
  );
}

/** Rename, change the expiry (empty: never), change the allowed IPs (empty: anywhere). */
function EditKey({ k, onCancel, onSaved }: { k: ApiKey; onCancel: () => void; onSaved: () => Promise<void> }) {
  const [name, setName] = useState(k.name ?? "");
  const [expires, setExpires] = useState(k.expiresAt ? k.expiresAt.slice(0, 10) : "");
  const [ips, setIps] = useState(k.ipAllow.join(", "));
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    const done = await call(`/api/dashboard/keys/${encodeURIComponent(k.id)}`, "PATCH", { name, expires_at: expires || null, ip_allow: ips });
    setBusy(false);
    if (done) await onSaved();
  };
  return (
    <form className="set-keyform" onSubmit={(e) => void submit(e)}>
      <ListRow as="label" className="set-row">
        <RowMain className="set-row__text"><RowTitle as="b">Name</RowTitle></RowMain>
        <RowTrail><input className="tset-pill" value={name} maxLength={60} placeholder="API key" onChange={(e) => setName(e.target.value)} /></RowTrail>
      </ListRow>
      <ListRow as="label" className="set-row">
        <RowMain className="set-row__text"><RowTitle as="b">Expires</RowTitle></RowMain>
        <RowTrail><input className="tset-pill" type="date" min={today} value={expires} onChange={(e) => setExpires(e.target.value)} /></RowTrail>
      </ListRow>
      <ListRow as="label" className="set-row">
        <RowMain className="set-row__text"><RowTitle as="b">Allowed IPs</RowTitle></RowMain>
        <RowTrail><input className="tset-pill" value={ips} placeholder="Any" onChange={(e) => setIps(e.target.value)} /></RowTrail>
      </ListRow>
      <ListRow as="div" className="set-row set-keyform__actions">
        <button type="button" className="tset-pill" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn--primary" disabled={busy}>Save</button>
      </ListRow>
    </form>
  );
}

function CreateKey({ data, labels, onCancel, onCreated }: {
  data: KeysSnapshot; labels: Record<string, string>; onCancel: () => void; onCreated: (t: { name: string; key: string }) => void;
}) {
  const usable = data.contexts.filter((x) => x.canCreate);
  const [name, setName] = useState("");
  const [org, setOrg] = useState(usable[0]?.org ?? "personal");
  const [scopes, setScopes] = useState<string[]>(["public.read"]);
  const [expires, setExpires] = useState("");
  const [ips, setIps] = useState("");
  const [busy, setBusy] = useState(false);
  const ctx = usable.find((x) => x.org === org);
  const offered = data.scopes.filter((s) => ctx?.scopes.includes(s.scope));
  const chosen = scopes.filter((s) => ctx?.scopes.includes(s));
  const toggle = (s: string, on: boolean) => setScopes((cur) => (on ? [...cur, s] : cur.filter((x) => x !== s)));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!chosen.length || busy) return;
    setBusy(true);
    const made = await call<{ name: string; key: string }>("/api/dashboard/keys", "POST", { name, org, scopes: chosen, ...(expires ? { expires_at: expires } : {}), ...(ips.trim() ? { ip_allow: ips } : {}) });
    setBusy(false);
    if (made) onCreated({ name: made.name, key: made.key });
  };
  const today = new Date().toISOString().slice(0, 10);

  return (
    <form className="set-keyform" onSubmit={(e) => void submit(e)}>
      <ListRow as="label" className="set-row">
        <RowMain className="set-row__text"><RowTitle as="b">Name</RowTitle></RowMain>
        <RowTrail><input className="tset-pill" value={name} maxLength={60} placeholder="API key" onChange={(e) => setName(e.target.value)} /></RowTrail>
      </ListRow>
      <ListRow as="label" className="set-row">
        <RowMain className="set-row__text"><RowTitle as="b">For</RowTitle></RowMain>
        <RowTrail><Dropdown className="tset-pill" label="For" value={org} onChange={setOrg} chevron={<ChevronDown size={14} aria-hidden />}
          options={usable.map((x) => ({ value: x.org, label: x.name }))} /></RowTrail>
      </ListRow>
      <fieldset className="set-row set-scopes">
        <legend className="set-row__text"><b>Scopes</b></legend>
        <div className="set-scopes__list">
          {offered.map((s) => (
            <label key={s.scope} title={s.description}>
              <input type="checkbox" checked={chosen.includes(s.scope)} onChange={(e) => toggle(s.scope, e.target.checked)} />
              {labels[s.scope] ?? s.scope}
            </label>
          ))}
        </div>
      </fieldset>
      <ListRow as="label" className="set-row">
        <RowMain className="set-row__text"><RowTitle as="b">Expires</RowTitle></RowMain>
        <RowTrail><input className="tset-pill" type="date" min={today} value={expires} onChange={(e) => setExpires(e.target.value)} /></RowTrail>
      </ListRow>
      <ListRow as="label" className="set-row">
        <RowMain className="set-row__text"><RowTitle as="b">Allowed IPs</RowTitle></RowMain>
        <RowTrail><input className="tset-pill" value={ips} placeholder="Any" onChange={(e) => setIps(e.target.value)} /></RowTrail>
      </ListRow>
      <ListRow as="div" className="set-row set-keyform__actions">
        <button type="button" className="tset-pill" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn--primary" disabled={!chosen.length || busy}>Create key</button>
      </ListRow>
    </form>
  );
}
